// Network + tracking primitives for the seller flow.
// Centralizes auth headers, error handling, and event tracking so stage
// modules don't repeat the boilerplate.

export const BETA_SECRET = process.env.REACT_APP_BETA_SECRET || "sixzero2026";
export const BETA_KEY = "od_beta_token";

const jsonHeaders = () => ({
  "Content-Type": "application/json",
  "x-beta-token": BETA_SECRET,
});

// Fire-and-forget analytics ping. Never throws.
export const track = (name, payload = {}) => {
  fetch("/api/events", {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ name, payload }),
    keepalive: true,
  }).catch(() => {});
};

// Attom property lookup. Returns the raw Attom response, or null on failure.
// When `includeOwner` is true the server also fetches Attom's expandedprofile
// endpoint and merges the owner-of-record block into `property[0].owner_record`.
export const lookupProperty = async (address, { includeOwner = false } = {}) => {
  try {
    const res = await fetch("/api/property", {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify({ address, include_owner: includeOwner }),
    });
    const data = await res.json();
    return data?.property?.[0] ? data : null;
  } catch {
    return null;
  }
};

// Single-turn Claude call. Returns plain text content (already joined).
// Throws on network / non-2xx so callers can decide what to do.
//
// Optional caching: pass `cacheKey` (typically "<stage>:<normalized_address>")
// and `cacheType` (one of: valuation, prep, disclosures_q, listing_copy,
// vendor_quote — see lib/cache.js DEFAULT_TTL). Server checks the cache before
// the Anthropic call and writes to it after. Saves tokens when multiple
// sellers analyze the same property (or one seller revisits).
// Pass `messages` for a multi-turn conversation (array of {role, content});
// falls back to a single-turn `prompt` when omitted.
// Pass `tools` (Anthropic tool-use schema) to let the model call actions —
// returned `toolCalls` is the list of { id, name, input } the model invoked.
export const callAI = async ({
  system, prompt, messages, tools, maxTokens = 2500, temperature, sessionId, propertyId,
  cacheKey, cacheType, cacheTtlSeconds,
}) => {
  const res = await fetch("/api/chat", {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({
      model: "claude-haiku-4-5-20251001",
      max_tokens: maxTokens,
      system,
      messages: messages || [{ role: "user", content: prompt }],
      mode: "seller",
      sessionId,
      propertyId,
      cache_key: cacheKey,
      cache_type: cacheType,
      cache_ttl_s: cacheTtlSeconds,
      ...(tools ? { tools } : {}),
      ...(temperature != null ? { temperature } : {}),
    }),
  });
  if (!res.ok) throw new Error(`chat ${res.status}`);
  const data = await res.json();
  return {
    text: data.content?.map((b) => b.text || "").join("") || "",
    toolCalls: (data.content || []).filter((b) => b.type === "tool_use").map((b) => ({ id: b.id, name: b.name, input: b.input })),
    sessionId: data.sessionId ?? sessionId ?? null,
    cached: Boolean(data.cached),
  };
};

// Normalize an address into a stable cache key fragment.
export const normalizeAddressKey = (a) =>
  (a || "").toLowerCase().replace(/\s+/g, " ").replace(/[.,]/g, "").trim();
